// filepath: apps/web/src/features/admin/templates/export-import/ExportButton.tsx
//
// FR-UI-ADM-13 AC-1 — per-row export button. Triggers the
// template bundle download via the existing JWT-authenticated
// GET /api/templates/{templateID}/versions/{version}/bundle
// endpoint. The blob response is converted into a browser
// download via URL.createObjectURL + a synthetic anchor click
// (`<a download="<template_id>-<version>.ascoa-template">`).
//
// DOWNLOAD TRIGGER (AC-1 verbatim):
//   "the SPA calls GET /api/templates/{templateID}/versions/{version}/bundle
//    with credentials and, on 200, triggers a browser download
//    of the response bytes as <template_id>-<version>.ascoa-template
//    using URL.createObjectURL(blob) + <a download="…"> click."
//
// INV-5: the blob bytes are NOT inspected. The download is a
// file side-effect (browser saves it under the user's chosen
// filename); the SPA never holds the parsed manifest in state.
// The static guard enforces the source-level contract
// (no `manifest_json` / `payload_zip` references in production
// source); the runtime contract is the file's binary
// transparency (the SPA treats the blob as opaque bytes).
//
// FR-UI-CORE-10: the export hook carries the typed union
// `TemplateBundleExportFetchError`. The button maps each kind to
// a typed error card below the action.

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import {
  classifyTemplateBundleExportError,
  useTemplateBundleExport,
} from "./useTemplateBundleExport";
import type { TemplateBundleExportFetchError } from "./types";

interface ExportButtonProps {
  templateID: string;
  version: string;
}

interface ErrorCard {
  message: string;
  testId: string;
}

function classifyError(
  err: Error | TemplateBundleExportFetchError | null,
): ErrorCard | null {
  if (!err) return null;
  // ApiError from a non-mapped status — run it through the
  // classifier.
  if (err instanceof ApiError) {
    const classified = classifyTemplateBundleExportError(err);
    return errorCard(classified);
  }
  // The hook always throws either an ApiError or a typed-union
  // error — both paths converge into a typed union.
  return errorCard(err as TemplateBundleExportFetchError);
}

function errorCard(err: TemplateBundleExportFetchError): ErrorCard {
  switch (err.kind) {
    case "permission_denied":
      return {
        message: err.message,
        testId: "template-bundle-export-permission-denied",
      };
    case "template_not_found":
      return {
        message: err.message,
        testId: "template-bundle-export-not-found",
      };
    case "bundle_too_large":
      return {
        message: err.message,
        testId: "template-bundle-export-too-large",
      };
    case "validation_failed":
      return {
        message: err.message,
        testId: "template-bundle-export-validation-failed",
      };
    case "rate_limit":
      return {
        message: err.message,
        testId: "template-bundle-export-rate-limit",
      };
    case "fetch_failure":
      return {
        message: err.message,
        testId: "template-bundle-export-fetch-failure",
      };
  }
}

/**
 * ExportButton — per-row export action. Triggers a browser
 * download of the bundle bytes via the existing JWT-gated GET
 * endpoint. The error surface is a single typed card below
 * the button (not a per-row card — the per-row card would be
 * visually overwhelming on a 20-row registry).
 *
 * The hook does NOT trigger the download itself; the button's
 * `onSuccess` callback handles the side-effect.
 */
export function ExportButton({
  templateID,
  version,
}: ExportButtonProps): React.ReactElement {
  const { t } = useTranslation();
  const exportMutation = useTemplateBundleExport();
  const [downloadError, setDownloadError] = useState<ErrorCard | null>(null);

  const handleExport = (): void => {
    setDownloadError(null);
    exportMutation.mutate(
      { templateID, version },
      {
        onSuccess: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${templateID}-${version}.ascoa-template`;
          // The anchor must be in the DOM for some browsers
          // (Safari) to honor the click. Append, click, remove.
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Defer revoke to the next tick so the browser has a
          // chance to start the download before the URL becomes
          // invalid.
          setTimeout(() => URL.revokeObjectURL(url), 0);
        },
        onError: (err) => {
          setDownloadError(classifyError(err as Error | null));
        },
      },
    );
  };

  return (
    <div className="space-y-1">
      <button
        type="button"
        disabled={exportMutation.isPending}
        onClick={handleExport}
        className="rounded bg-secondary px-3 py-2 text-xs font-medium text-secondary-foreground hover:bg-secondary/90 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="template-bundle-export-button"
        aria-label={t(
          "admin.templates.export_import.action.export_aria",
          undefined,
          {
            template_id: templateID,
            version,
          },
        )}
        style={{ minHeight: "48px", minWidth: "48px" }}
      >
        {exportMutation.isPending
          ? t("admin.templates.export_import.action.exporting")
          : t("admin.templates.export_import.action.export")}
      </button>
      {downloadError ? (
        <div
          className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700"
          role="alert"
          data-testid={downloadError.testId}
        >
          {downloadError.message}
        </div>
      ) : null}
    </div>
  );
}
